"use client"

import { useEffect, useRef } from "react"
import type { Subscription } from "@rails/actioncable"
import { useQueryClient } from "@tanstack/react-query"

import { getCable } from "@/lib/actionCable"
import { useDevAuth as useAuth } from "@/hooks/use-dev-auth"

type NotificationReadEvent = {
  type: "notification_read"
  notification_id?: number | string
  unread_count: number
}

type AllNotificationsReadEvent = {
  type: "all_notifications_read"
  unread_count: number
}

type NotificationsInvalidatedEvent = {
  type: "notifications_invalidated"
  unread_count: number
}

type NotificationBroadcast = {
  id?: string | number
  guid?: string
  message?: string
  type?: string
  [key: string]: unknown
}

type NotificationsChannelMessage =
  | NotificationReadEvent
  | AllNotificationsReadEvent
  | NotificationsInvalidatedEvent
  | NotificationBroadcast

export function useNotificationsCable(enabled: boolean = true) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const subscriptionRef = useRef<Subscription | null>(null)

  useEffect(() => {
    if (!enabled) return

    const setup = async () => {
      const token = await getToken()
      if (!token) return

      const cable = getCable(token)
      if (!cable) return

      subscriptionRef.current = cable.subscriptions.create(
        { channel: "NotificationsChannel" },
        {
          received: (data: NotificationsChannelMessage) => {
            // Invalidate all notification queries (prefix match covers all bookId variants)
            if (
              data?.type === "notification_read" ||
              data?.type === "all_notifications_read" ||
              data?.type === "notifications_invalidated" ||
              (data as NotificationBroadcast)?.id ||
              (data as NotificationBroadcast)?.guid ||
              (data as NotificationBroadcast)?.message
            ) {
              queryClient.invalidateQueries({ queryKey: ["notifications"] })
            }
          },
        }
      )
    }

    setup()

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
        subscriptionRef.current = null
      }
    }
  }, [enabled, getToken, queryClient])
}
